'use client'

import { useMemo } from 'react'
import {
  resolveLinkedPeople,
  useDocumentView,
  useDocumentStatus,
} from '@/document'
import { formatDateString } from '@/lib/date'
import type { Entry, EntryWithPeople } from '@/document'

export type { EntryWithPeople }

export interface TaskMetadata {
  status?: string
  priority?: string
  important?: boolean
  category?: string
  dueDate?: string
  completedAt?: string
}

export interface UpcomingTasksData {
  overdue: EntryWithPeople[]
  today: EntryWithPeople[]
  nextWeek: EntryWithPeople[]
  thisMonth: EntryWithPeople[]
  later: EntryWithPeople[]
}

/**
 * Hook to fetch and group tasks by due date into time-based buckets.
 * Returns all incomplete tasks (with or without due dates).
 *
 * Time buckets:
 * - Overdue: dueDate < today
 * - Today: dueDate === today
 * - Next Week: today < dueDate <= 7 days from now
 * - This Month: 7 days < dueDate <= end of current month
 * - Later: dueDate > end of current month OR no dueDate (unscheduled)
 */
export function useUpcomingTasks() {
  const doc = useDocumentView()
  const ready = useDocumentStatus()

  const data = useMemo(() => {
    if (!ready || !doc) return null

    try {
      // Get today's date in local timezone as YYYY-MM-DD string
      // IMPORTANT: Don't use toISOString() as it converts to UTC, causing timezone bugs
      const today = new Date()
      const todayStr = formatDateString(today)

      // Calculate boundary dates in local timezone
      const sevenDaysFromNow = new Date(today)
      sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7)
      const sevenDaysStr = formatDateString(sevenDaysFromNow)

      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      const endOfMonthStr = formatDateString(endOfMonth)

      // Get all incomplete tasks (with or without due dates)
      const incompleteTasks = Object.values(doc.entries).filter((entry) => {
        if (entry.type !== 'task' || entry.archivedAt) return false
        const metadata = entry.metadata as TaskMetadata | undefined
        if (metadata?.status === 'done') return false
        return true
      })

      // Helper to attach people and topics to entries
      const withPeople = (entry: Entry): EntryWithPeople => {
        const linkedPeople = resolveLinkedPeople(entry.id, doc)
        const people = linkedPeople.map((p) => ({
          id: p.id,
          name: p.title,
        }))

        const topics = (entry.topicIds || [])
          .map((topicId) => doc.topics[topicId])
          .filter((topic) => topic)
          .map((topic) => ({
            id: topic.id,
            name: topic.name,
          }))

        return {
          ...entry,
          people,
          topics,
        }
      }

      // Group tasks by time buckets
      const overdue: EntryWithPeople[] = []
      const today_: EntryWithPeople[] = []
      const nextWeek: EntryWithPeople[] = []
      const thisMonth: EntryWithPeople[] = []
      const later: EntryWithPeople[] = []

      incompleteTasks.forEach((task) => {
        const metadata = task.metadata as TaskMetadata
        const dueDate = metadata.dueDate

        if (!dueDate) {
          // Tasks without dueDate go to Later (unscheduled)
          later.push(withPeople(task))
        } else if (dueDate < todayStr) {
          overdue.push(withPeople(task))
        } else if (dueDate === todayStr) {
          today_.push(withPeople(task))
        } else if (dueDate <= sevenDaysStr) {
          nextWeek.push(withPeople(task))
        } else if (dueDate <= endOfMonthStr) {
          thisMonth.push(withPeople(task))
        } else {
          later.push(withPeople(task))
        }
      })

      // Sort each group by due date ascending (earliest first)
      // Tasks without due dates sort to the end
      const sortByDueDate = (a: EntryWithPeople, b: EntryWithPeople) => {
        const aDate = (a.metadata as TaskMetadata).dueDate
        const bDate = (b.metadata as TaskMetadata).dueDate

        // Unscheduled tasks (no dueDate) sort to the end
        if (!aDate && !bDate) return 0
        if (!aDate) return 1
        if (!bDate) return -1

        return aDate.localeCompare(bDate)
      }

      overdue.sort(sortByDueDate)
      today_.sort(sortByDueDate)
      nextWeek.sort(sortByDueDate)
      thisMonth.sort(sortByDueDate)
      later.sort(sortByDueDate)

      return {
        overdue,
        today: today_,
        nextWeek,
        thisMonth,
        later,
      } as UpcomingTasksData
    } catch (error) {
      console.error('Error loading upcoming tasks:', error)
      return null
    }
  }, [doc, ready])

  return {
    data,
    isLoading: !ready,
    error: null,
    isSuccess: ready,
    isError: false,
  }
}
